import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { defaultIfNullish } from 'src/app/shared/util/helper.util';
import {
  CompareOperator,
  DescriptiveTextConfig,
  DescriptiveTextTypeEnum,
  DisplayFormatEnum,
  FormTemplateFieldAutopopulateConfig,
  MapConditionConfig,
  TransformValueConfig,
  TransformValueTypeEnum
} from './autopopulate.model';

@Injectable({
  providedIn: 'root'
})
export class AutopopulateService {
  constructor(private datePipe: DatePipe) {}

  private DEBUG_MODE = false; // SET TO TRUE ONLY FOR DEBUGGING

  populateFormGroup(
    formGroup: FormGroup,
    autopopulateConfig: { [key: string]: FormTemplateFieldAutopopulateConfig[] },
    incomingData: any,
    resetControls?: boolean
  ) {
    incomingData = this.flattenByOneLevel(incomingData);
    Object.keys(autopopulateConfig).forEach(key => {
      if (this.DEBUG_MODE) {
        console.log(`*******************\nMaps to key: ${key}\n`);
      }
      const destinationFormControl: FormControl = this.getFormControl(formGroup, key);
      if (destinationFormControl) {
        if (resetControls) {
          destinationFormControl.reset();
        }
        autopopulateConfig[key].forEach(autopopulateFieldConfig => {
          const autoPopulateMapConditionConfig = autopopulateFieldConfig.mapConditionConfig;
          const incomingDataKeyPath = autopopulateFieldConfig.incomingDataValuePath;
          const incomingDataTransformConfig = autopopulateFieldConfig.transformValueConfig;

          const incomingValue = this.getJsonDataViaDotNotation(incomingDataKeyPath, incomingData);
          if (this.DEBUG_MODE) {
            console.log(
              `incoming key path: ${incomingDataKeyPath}\nincoming value: ${incomingValue}`
            );
          }
          const mapConditionSatisfied = this.evaluateMapCondition(
            autoPopulateMapConditionConfig,
            incomingValue
          );

          if (mapConditionSatisfied) {
            const transformedValue = this.transformValue(
              destinationFormControl,
              incomingDataTransformConfig,
              incomingValue
            );
            this.formatAndApplyValue(
              destinationFormControl,
              incomingDataTransformConfig.displayFormat,
              transformedValue
            );
            if (this.DEBUG_MODE) {
              console.log(`populated value: ${destinationFormControl.value}\n*******************`);
            }
          }
        });
      } else {
        // console.warn('Control does not exist: ' + key);
      }
    });
  }

  getJsonDataViaDotNotation(path: string, json: JSON) {
    try {
      const data = path.split('.').reduce((a, b) => a[b], json);

      return data;
    } catch {
      // console.warn('Unable to find JSON value: ' + path);
      return null;
    }
  }

  evaluateMapCondition(mapConditionConfig: MapConditionConfig, incomingValue: any): boolean {
    const res = this.compareValues(
      incomingValue,
      mapConditionConfig.comparisonValue || null,
      mapConditionConfig.comparisonOperator
    );
    if (this.DEBUG_MODE) {
      console.log(`map condition res: ${res}\n`);
    }
    return res;
  }

  transformValue(
    formControl: FormControl,
    transformValueConfig: TransformValueConfig,
    incomingValue: any
  ) {
    const typeConfig = transformValueConfig.typeConfig;

    if (typeConfig['value']) {
      incomingValue = typeConfig['value'];
    }
    incomingValue = this.applyDescriptiveText(
      transformValueConfig.descriptiveTextConfig,
      incomingValue
    );
    if (this.DEBUG_MODE) {
      console.log(`Transform value type: ${typeConfig.type}\n`);
    }

    switch (typeConfig.type) {
      case TransformValueTypeEnum.none: {
        return incomingValue;
      }
      case TransformValueTypeEnum.specifyValue: {
        // formControl.patchValue(incomingValue);
        return typeConfig.value;
      }
      case TransformValueTypeEnum.sum: {
        if (!isNaN(incomingValue)) {
          // formControl.patchValue((formControl.value || 0) + incomingValue);
          return (formControl.value || 0) + incomingValue;
        }
        break;
      }
      case TransformValueTypeEnum.date: {
        let date = new Date(incomingValue);

        if (isNaN(date.getTime()) || date.toString() === 'Invalid Date') {
          return null;
        }

        return this.datePipe.transform(date, typeConfig.format);
      }
      case TransformValueTypeEnum.map: {
        if (typeConfig.map[incomingValue] !== undefined) {
          return typeConfig.map[incomingValue];
        }
        return incomingValue;
      }
      case TransformValueTypeEnum.scale: {
        if (!isNaN(incomingValue)) {
          return Math.round(incomingValue * typeConfig.multiplier);
        }
        break;
      }
      case TransformValueTypeEnum.custom: {
        return typeConfig.fn(formControl, incomingValue);
      }
    }
  }

  formatAndApplyValue(
    destinationFormControl: FormControl,
    displayFormat: DisplayFormatEnum,
    incomingValue: any
  ) {
    switch (displayFormat) {
      case DisplayFormatEnum.appendText: {
        if (incomingValue === null || incomingValue === undefined) {
          return;
        }
        let currentDestinationValue = defaultIfNullish(destinationFormControl.value, '');

        if (!(destinationFormControl.value || '').includes(incomingValue)) {
          destinationFormControl.patchValue(currentDestinationValue + incomingValue + '\n');
        }
        break;
      }
      case DisplayFormatEnum.medication: {
        if (incomingValue === null || incomingValue === undefined) {
          return;
        }

        // Destination value should be an array
        let currentDestinationValue = defaultIfNullish(destinationFormControl.value, []);
        if (typeof currentDestinationValue == 'string' && currentDestinationValue != '') {
          currentDestinationValue = currentDestinationValue.split(',');
        } else if (!Array.isArray(currentDestinationValue)) {
          currentDestinationValue = [];
        }

        if (typeof incomingValue === 'string') {
          incomingValue = incomingValue.split(',');
        }

        if (Array.isArray(incomingValue)) {
          const newValues = incomingValue.filter(incoming => {
            const hasId = incoming && typeof incoming === 'object' && 'id' in incoming;

            return hasId
              ? !currentDestinationValue.some(current => current && current.id === incoming.id)
              : !currentDestinationValue.includes(incoming);
          });

          if (newValues.length > 0) {
            destinationFormControl.patchValue([...currentDestinationValue, ...newValues]);
          }
        }
        break;
      }
      case DisplayFormatEnum.list: {
        if (incomingValue === null || incomingValue === undefined) {
          return;
        }
        let currentDestinationValue = defaultIfNullish(destinationFormControl.value, []);

        if (typeof incomingValue === 'string') {
          incomingValue = incomingValue.split(',');
        }

        if (!currentDestinationValue.includes(incomingValue)) {
          destinationFormControl.patchValue(currentDestinationValue + '\n' + incomingValue);
        }
        break;
      }
      case DisplayFormatEnum.none: {
        if (incomingValue === undefined) {
          // A transform (e.g. sum/scale on a non-numeric value) can yield undefined;
          // never overwrite the control with it.
          return;
        }
        destinationFormControl.patchValue(incomingValue);
        break;
      }
    }
  }

  applyDescriptiveText(descriptiveTextConfig: DescriptiveTextConfig, incomingValue: any) {
    if (!descriptiveTextConfig) return incomingValue;
    // console.log('applying descriptive text');
    switch (descriptiveTextConfig.type) {
      case DescriptiveTextTypeEnum.pre: {
        return descriptiveTextConfig.preDescriptiveText + incomingValue;
      }
      case DescriptiveTextTypeEnum.post: {
        return incomingValue + descriptiveTextConfig.postDescriptiveText;
      }
      case DescriptiveTextTypeEnum.preAndPost: {
        return (
          descriptiveTextConfig.preDescriptiveText +
          incomingValue +
          descriptiveTextConfig.postDescriptiveText
        );
      }
    }
  }

  public compareValues(
    incomingValue: any,
    comparisonValue: any,
    operator: CompareOperator
  ): boolean {
    if (incomingValue === undefined) return false;
    // console.log('comparing: ' + incomingValue + ' with ' + comparisonValue);
    switch (operator) {
      case '>': {
        return incomingValue > comparisonValue;
      }
      case '>=': {
        return incomingValue >= comparisonValue;
      }
      case '<': {
        return incomingValue < comparisonValue;
      }
      case '<=': {
        return incomingValue <= comparisonValue;
      }
      case '=': {
        return incomingValue === comparisonValue;
      }
      case '!=': {
        return incomingValue !== comparisonValue;
      }
      case '!empty': {
        if (
          (typeof incomingValue === 'string' || Array.isArray(incomingValue)) &&
          incomingValue.length > 0
        ) {
          return true;
        }
        return false;
      }
      case 'none': {
        return true;
      }
      // case 'includes': {
      //   return patientData.includes(conditionData);
      // }
    }
  }

  getFormControl(formGroup: FormGroup, key: string) {
    let control: any = formGroup;
    const keys = key.split('.');
    if (keys.length === 1) {
      return formGroup.controls[key];
    }
    keys.forEach(k => {
      try {
        control = control['controls'][k];
      } catch (e) {
        // console.warn('Control not found: ' + k);
        return null;
      }
    });
    return control;
  }

  flattenByOneLevel(data: JSON) {
    if (data === null || data === undefined) {
      return {};
    }
    const res = Object.values(data).reduce((acc, val) => {
      acc = { ...acc, ...val };
      return acc;
    }, {});
    // console.log('flattened', res);
    return res;
  }
}
